import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

export function GeographyCard({ ticket, onChange, regions, onTaxonomyUpdated }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [addingRegion, setAddingRegion] = useState(false);
  const [addingLocation, setAddingLocation] = useState(false);
  const regionList = Array.isArray(regions) ? regions : [];
  const currentRegion = ticket.region || "";
  const currentLocation = ticket.location || "";
  const activeRegionCfg = regionList.find((r) => r.name === currentRegion);
  const locationsForRegion = activeRegionCfg?.locations || [];
  const suggestedRegion = ticket.region_suggested;
  const suggestedLocation = ticket.location_suggested;

  const changeRegion = (val) => {
    const nextRegion = val === "none" ? null : val;
    const nextLocation = (() => {
      if (!nextRegion) return null;
      const cfg = regionList.find((r) => r.name === nextRegion);
      if (!cfg) return null;
      if (currentLocation && (cfg.locations || []).includes(currentLocation)) return currentLocation;
      return null;
    })();
    onChange({ region: nextRegion, location: nextLocation });
  };

  const changeLocation = (val) => {
    onChange({ location: val === "none" ? null : val });
  };

  const addSuggestedRegion = async () => {
    if (!suggestedRegion) return;
    setAddingRegion(true);
    try {
      await api.post("/settings/taxonomy/add", { list_key: "regions", value: suggestedRegion });
      await onTaxonomyUpdated?.();
      await onChange({ region: suggestedRegion, region_suggested: null });
      toast.success(`Added region "${suggestedRegion}" and applied it to this ticket`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add region");
    } finally { setAddingRegion(false); }
  };

  const addSuggestedLocation = async () => {
    if (!suggestedLocation || !currentRegion) return;
    setAddingLocation(true);
    try {
      await api.post("/settings/taxonomy/add", { list_key: "regions", region: currentRegion, value: suggestedLocation });
      await onTaxonomyUpdated?.();
      await onChange({ location: suggestedLocation, location_suggested: null });
      toast.success(`Added "${suggestedLocation}" under ${currentRegion} and applied it`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add location");
    } finally { setAddingLocation(false); }
  };

  if (regionList.length === 0) return null;

  return (
    <div className="hairline rounded-lg p-3 bg-white space-y-2" data-testid="ticket-geography-card">
      <div className="text-[11px] uppercase text-slate-500 tracking-wider font-medium">Geography</div>

      <div>
        <Label className="text-[11px] uppercase text-slate-500 tracking-wider">Region</Label>
        <Select value={currentRegion || "none"} onValueChange={changeRegion}>
          <SelectTrigger data-testid="ticket-region-dropdown" className="mt-1 bg-[#FFFFFF] border-slate-200 h-9">
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-200">
            <SelectItem value="none">— none —</SelectItem>
            {regionList.map((r) => (
              <SelectItem key={r.name} value={r.name}>{r.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-[11px] uppercase text-slate-500 tracking-wider">Location</Label>
        <Select
          value={currentLocation || "none"}
          onValueChange={changeLocation}
          disabled={!currentRegion || locationsForRegion.length === 0}
        >
          <SelectTrigger data-testid="ticket-location-dropdown" className="mt-1 bg-[#FFFFFF] border-slate-200 h-9">
            <SelectValue placeholder={currentRegion ? (locationsForRegion.length ? "—" : "No locations under this region") : "Pick a region first"} />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-200">
            <SelectItem value="none">— none —</SelectItem>
            {locationsForRegion.map((loc) => (
              <SelectItem key={loc} value={loc}>{loc}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {(suggestedRegion || suggestedLocation) && (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-800 space-y-1.5"
          data-testid="ticket-geo-suggestion"
        >
          <div className="font-medium">AI suggested (not in taxonomy)</div>
          <div className="font-mono">
            {suggestedRegion && <span>region: {suggestedRegion}</span>}
            {suggestedRegion && suggestedLocation && <span> · </span>}
            {suggestedLocation && <span>location: {suggestedLocation}</span>}
          </div>
          {isAdmin ? (
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              {suggestedRegion && (
                <button
                  type="button"
                  onClick={addSuggestedRegion}
                  disabled={addingRegion}
                  className="inline-flex items-center gap-1 rounded border border-amber-400 bg-white text-amber-900 hover:bg-amber-100 px-2 py-1 text-[11px] font-medium disabled:opacity-50"
                  data-testid="geo-add-suggested-region"
                >
                  <Plus size={11} /> {addingRegion ? "Adding…" : `Add region "${suggestedRegion}"`}
                </button>
              )}
              {suggestedLocation && (
                <button
                  type="button"
                  onClick={addSuggestedLocation}
                  disabled={addingLocation || !currentRegion}
                  title={currentRegion ? "" : "Set a region on this ticket first"}
                  className="inline-flex items-center gap-1 rounded border border-amber-400 bg-white text-amber-900 hover:bg-amber-100 px-2 py-1 text-[11px] font-medium disabled:opacity-50"
                  data-testid="geo-add-suggested-location"
                >
                  <Plus size={11} /> {addingLocation ? "Adding…" : `Add location "${suggestedLocation}"${currentRegion ? ` to ${currentRegion}` : ""}`}
                </button>
              )}
            </div>
          ) : (
            <div className="text-amber-700">Ask an admin to add these in Settings → Regions &amp; locations to enable filtering.</div>
          )}
          {isAdmin && suggestedLocation && !currentRegion && (
            <div className="text-amber-700 pt-0.5">Set a region above first to add this location.</div>
          )}
        </div>
      )}
    </div>
  );
}
